// =====================================================
// SOCIETIES ADMIN PAGE - Con import/export Excel
// =====================================================

import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
  Building2,
  Plus,
  Search,
  Filter,
  RefreshCw,
  Edit2,
  Power,
  Loader2,
  Save,
  X,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Star,
  Link2,
  MapPin,
  Check,
  FileSpreadsheet,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { societiesApi } from '../../api/societies';
import { societiesImportExport } from '../../api/import-export';
import { Modal, ModalFooter } from '../../components/ui/Modal';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { ImportExportModal } from '../../components/ui/ImportExportModal';
import { toast } from '../../components/ui/Toast';
import type { Society, SocietyFormData, CreateSocietyRequest, UpdateSocietyRequest } from '../../types/catalog-admin';
import type { PaginatedResponse } from '../../types/pagination';

type SortField = 'name' | 'code' | 'createdAt';

interface SocietyLinkedSite {
  siteId: string;
  siteCode: string;
  siteName: string;
  isLinked: boolean;
  isDefault: boolean;
}

interface SocietyWithSiteInfo extends Society {
  isDefaultInCurrentSite?: boolean;
}

const validateIBAN = (value: string | undefined): boolean | string => {
  if (!value) return true;
  const cleaned = value.replace(/\s/g, '').toUpperCase();
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/.test(cleaned)) {
    return 'IBAN no válido';
  }
  return true;
};

export function SocietiesAdminPage() {
  const { activeSite } = useAuth();
  
  const [data, setData] = useState<PaginatedResponse<SocietyWithSiteInfo> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showFilters, setShowFilters] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [filterByCurrentSite, setFilterByCurrentSite] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Society | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkingSociety, setLinkingSociety] = useState<Society | null>(null);
  const [linkedSites, setLinkedSites] = useState<SocietyLinkedSite[]>([]);
  const [isLoadingLinks, setIsLoadingLinks] = useState(false);
  const [isSavingLinks, setIsSavingLinks] = useState(false);

  const [isImportExportOpen, setIsImportExportOpen] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    item: Society | null;
    action: 'activate' | 'deactivate';
  }>({ isOpen: false, item: null, action: 'deactivate' });
  const [isProcessing, setIsProcessing] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<Omit<SocietyFormData, 'isDefault'>>({
    defaultValues: { code: '', name: '', legalName: '', cif: '', address: '', city: '', postalCode: '', province: '', bankName: '', iban: '', swift: '' },
  });

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params: any = {
        page, limit, search: searchQuery || undefined, sortBy, sortOrder, includeInactive: showInactive,
      };
      if (activeSite) params.currentSiteId = activeSite.id;
      if (filterByCurrentSite && activeSite) params.siteId = activeSite.id;

      const result = await societiesApi.getAllPaginated(params);
      setData(result as PaginatedResponse<SocietyWithSiteInfo>);
    } catch (err: any) {
      setError(err.message || 'Error al cargar sociedades');
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, searchQuery, sortBy, sortOrder, showInactive, filterByCurrentSite, activeSite]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSearch = (e: React.FormEvent) => { e.preventDefault(); setPage(1); };

  const handleCreate = () => {
    setEditingItem(null);
    setFormError(null);
    reset({ code: '', name: '', legalName: '', cif: '', address: '', city: '', postalCode: '', province: '', bankName: '', iban: '', swift: '' });
    setIsModalOpen(true);
  };

  const handleEdit = (item: Society) => {
    setEditingItem(item);
    setFormError(null);
    reset({
      code: item.code, name: item.name, legalName: item.legalName || '', cif: item.cif,
      address: item.address || '', city: item.city || '', postalCode: item.postalCode || '',
      province: item.province || '', bankName: item.bankName || '', iban: item.iban || '', swift: item.swift || '',
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (!isSaving) {
      setIsModalOpen(false);
      setEditingItem(null);
      setFormError(null);
    }
  };

  const handleSave = async (formData: Omit<SocietyFormData, 'isDefault'>) => {
    // Limpiar error previo
    setFormError(null);

    // Validar código duplicado (global)
    const codeUpper = formData.code.toUpperCase().trim();
    const existingByCode = data?.data.find(
      (item) => item.code.toUpperCase() === codeUpper && item.id !== editingItem?.id
    );

    if (existingByCode) {
      setFormError(`Ya existe una sociedad con el código "${codeUpper}"`);
      return;
    }

    // Validar CIF duplicado (global)
    const cifUpper = formData.cif.toUpperCase().trim();
    const existingByCif = data?.data.find(
      (item) => item.cif.toUpperCase() === cifUpper && item.id !== editingItem?.id
    );

    if (existingByCif) {
      setFormError(`Ya existe una sociedad con el CIF "${cifUpper}"`);
      return;
    }

    setIsSaving(true);
    try {
      if (editingItem) {
        const updateData: UpdateSocietyRequest = {
          code: formData.code, name: formData.name, legalName: formData.legalName || undefined,
          cif: formData.cif, address: formData.address || undefined, city: formData.city || undefined,
          postalCode: formData.postalCode || undefined, province: formData.province || undefined,
          bankName: formData.bankName || undefined, iban: formData.iban?.replace(/\s/g, '').toUpperCase() || undefined,
          swift: formData.swift || undefined,
        };
        await societiesApi.update(editingItem.id, updateData);
        toast.success('Actualizado', `Sociedad "${formData.name}" actualizada`);
      } else {
        const createData: CreateSocietyRequest = {
          code: formData.code, name: formData.name, legalName: formData.legalName || undefined,
          cif: formData.cif, address: formData.address || undefined, city: formData.city || undefined,
          postalCode: formData.postalCode || undefined, province: formData.province || undefined,
          bankName: formData.bankName || undefined, iban: formData.iban?.replace(/\s/g, '').toUpperCase() || undefined,
          swift: formData.swift || undefined,
        };
        await societiesApi.create(createData);
        toast.success('Creado', `Sociedad "${formData.name}" creada`);
      }
      setIsModalOpen(false);
      setEditingItem(null);
      setFormError(null);
      loadData();
    } catch (err: any) {
      // Detectar si el error es por código o CIF duplicado
      const errorMsg = err.message?.toLowerCase() || '';
      if (errorMsg.includes('cif')) {
        setFormError(`Ya existe una sociedad con el CIF "${formData.cif.toUpperCase().trim()}"`);
      } else {
        setFormError(`Ya existe una sociedad con el código "${formData.code.toUpperCase().trim()}"`);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenLinkModal = async (item: Society) => {
    setLinkingSociety(item);
    setIsLinkModalOpen(true);
    setIsLoadingLinks(true);
    try {
      const sites = await societiesApi.getLinkedSites(item.id);
      setLinkedSites(sites);
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudieron cargar los centros');
      setIsLinkModalOpen(false);
    } finally {
      setIsLoadingLinks(false);
    }
  };

  const handleCloseLinkModal = () => {
    if (!isSavingLinks) { setIsLinkModalOpen(false); setLinkingSociety(null); setLinkedSites([]); }
  };

  const handleToggleSiteLink = (siteId: string) => {
    setLinkedSites(prev => prev.map(site => {
      if (site.siteId === siteId) {
        const newIsLinked = !site.isLinked;
        return { ...site, isLinked: newIsLinked, isDefault: newIsLinked ? site.isDefault : false };
      }
      return site;
    }));
  };

  const handleToggleSiteDefault = (siteId: string) => {
    setLinkedSites(prev => prev.map(site => {
      if (site.siteId === siteId) {
        if (!site.isLinked) return site;
        return { ...site, isDefault: !site.isDefault };
      }
      return site;
    }));
  };

  const handleSaveLinks = async () => {
    if (!linkingSociety) return;
    setIsSavingLinks(true);
    try {
      await societiesApi.updateSiteLinks(linkingSociety.id, {
        links: linkedSites.map(s => ({ siteId: s.siteId, isLinked: s.isLinked, isDefault: s.isDefault })),
      });
      toast.success('Guardado', `Vinculaciones actualizadas para "${linkingSociety.name}"`);
      setIsLinkModalOpen(false);
      setLinkingSociety(null);
      loadData();
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudieron guardar las vinculaciones');
    } finally {
      setIsSavingLinks(false);
    }
  };

  const handleToggleStatus = (item: Society) => {
    setConfirmDialog({ isOpen: true, item, action: item.isActive ? 'deactivate' : 'activate' });
  };

  const handleConfirmToggle = async () => {
    if (!confirmDialog.item) return;
    setIsProcessing(true);
    try {
      if (confirmDialog.action === 'deactivate') {
        await societiesApi.deactivate(confirmDialog.item.id);
        toast.success('Desactivado', `Sociedad "${confirmDialog.item.name}" desactivada`);
      } else {
        await societiesApi.reactivate(confirmDialog.item.id);
        toast.success('Activado', `Sociedad "${confirmDialog.item.name}" activada`);
      }
      loadData();
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo cambiar el estado');
    } finally {
      setIsProcessing(false);
      setConfirmDialog({ isOpen: false, item: null, action: 'deactivate' });
    }
  };

  const items = data?.data || [];
  const meta = data?.meta;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sociedades</h1>
          <p className="text-sm text-gray-500 mt-1">Gestión de sociedades facturadoras</p>
        </div>
      </div>

      {/* Toolbar - Nuevo diseño */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-4">
        {/* Fila superior: Búsqueda y acciones principales */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* Búsqueda y botón filtros */}
          <div className="flex items-center flex-1 max-w-2xl gap-2">
            <form onSubmit={handleSearch} className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar por código, nombre o CIF..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </form>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                showFilters 
                  ? 'bg-blue-50 border-blue-200 text-blue-600' 
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filtros
            </button>
          </div>

          {/* Acciones */}
          <div className="flex items-center gap-2">
            <button
              onClick={loadData}
              disabled={isLoading}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Actualizar"
            >
              <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setIsImportExportOpen(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-blue-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FileSpreadsheet className="h-4 w-4" />
              Importar / Exportar
            </button>
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 shadow-md shadow-blue-100 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Nueva sociedad
            </button>
          </div>
        </div>

        {/* Fila de filtros activos (chips) */}
        {(showInactive || searchQuery || filterByCurrentSite) && (
          <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mr-2">
              Filtros activos:
            </span>
            
            {showInactive && (
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                Estado: Incluye inactivos
                <button 
                  onClick={() => { setShowInactive(false); setPage(1); }}
                  className="ml-1.5 hover:text-blue-900"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}

            {filterByCurrentSite && activeSite && (
              <div className="inline-flex items-center bg-green-50 text-green-700 px-3 py-1 rounded-full text-xs font-medium">
                Centro: {activeSite.name}
                <button 
                  onClick={() => { setFilterByCurrentSite(false); setPage(1); }}
                  className="ml-1.5 hover:text-green-900"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            
            {searchQuery && (
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                Búsqueda: "{searchQuery}"
                <button 
                  onClick={() => { setSearchQuery(''); setPage(1); }}
                  className="ml-1.5 hover:text-blue-900"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            
            <button 
              onClick={() => {
                setShowInactive(false);
                setFilterByCurrentSite(false);
                setSearchQuery('');
                setPage(1);
              }}
              className="text-xs text-gray-400 hover:text-red-500 font-medium ml-2 underline underline-offset-2"
            >
              Limpiar todo
            </button>
          </div>
        )}

        {/* Fila inferior: Ordenación y paginación */}
        {showFilters && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between border-t border-gray-100 pt-3 gap-4">
            {/* Checkboxes y ordenación */}
            <div className="flex items-center gap-6 flex-wrap">
              <label className="inline-flex items-center cursor-pointer group">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => {
                    setShowInactive(e.target.checked);
                    setPage(1);
                  }}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-600 group-hover:text-gray-900 transition-colors">
                  Mostrar inactivos
                </span>
              </label>

              <label className={`inline-flex items-center cursor-pointer group ${!activeSite ? 'opacity-50' : ''}`}>
                <input
                  type="checkbox"
                  checked={filterByCurrentSite}
                  onChange={(e) => {
                    setFilterByCurrentSite(e.target.checked);
                    setPage(1);
                  }}
                  disabled={!activeSite}
                  className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <span className="ml-2 text-sm text-gray-600 group-hover:text-gray-900 transition-colors">
                  Solo del centro actual
                  {activeSite && <span className="text-green-600 ml-1">({activeSite.name})</span>}
                </span>
              </label>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Ordenar por:</span>
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field as SortField);
                    setSortOrder(order as 'asc' | 'desc');
                    setPage(1);
                  }}
                  className="text-sm border-none bg-transparent py-0 pl-0 pr-8 font-medium text-gray-700 focus:ring-0 cursor-pointer hover:text-blue-600 transition-colors"
                >
                  <option value="name-asc">Nombre (A-Z)</option>
                  <option value="name-desc">Nombre (Z-A)</option>
                  <option value="code-asc">Código (A-Z)</option>
                  <option value="code-desc">Código (Z-A)</option>
                  <option value="createdAt-desc">Fecha (Más reciente)</option>
                  <option value="createdAt-asc">Fecha (Antiguo)</option>
                </select>
              </div>
            </div>

            {/* Items por página */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">Ver:</span>
              <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => { setLimit(10); setPage(1); }}
                  className={`px-3 py-1 text-xs font-semibold border-r border-gray-200 transition-colors ${
                    limit === 10 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                  }`}
                >
                  10
                </button>
                <button
                  onClick={() => { setLimit(25); setPage(1); }}
                  className={`px-3 py-1 text-xs font-semibold border-r border-gray-200 transition-colors ${
                    limit === 25 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                  }`}
                >
                  25
                </button>
                <button
                  onClick={() => { setLimit(50); setPage(1); }}
                  className={`px-3 py-1 text-xs font-semibold transition-colors ${
                    limit === 50 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                  }`}
                >
                  50
                </button>
              </div>
              <span className="text-sm text-gray-500">por página</span>
            </div>
          </div>
        )}
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {items.length === 0 && !isLoading ? (
          <div className="p-12 text-center">
            <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">{searchQuery || filterByCurrentSite ? 'Sin resultados' : 'No hay sociedades'}</h3>
            <p className="text-gray-500">{filterByCurrentSite ? `No hay sociedades vinculadas al centro "${activeSite?.name}"` : searchQuery ? 'No se encontraron sociedades con ese criterio' : 'Crea tu primera sociedad'}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Código</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Nombre</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">CIF</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">IBAN</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Centros</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Estado</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((item) => (
                    <tr key={item.id} className={`hover:bg-gray-50 ${!item.isActive ? 'bg-gray-50/50' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="px-2.5 py-1 bg-amber-50 text-amber-700 text-xs font-semibold rounded-lg">{item.code}</span>
                          {item.isDefaultInCurrentSite && activeSite && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-semibold rounded-full" title={`Sociedad por defecto en ${activeSite.name}`}>
                              <Star className="h-3 w-3 fill-green-500" /> Default en {activeSite.code || activeSite.name}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap"><span className={`font-medium ${item.isActive ? 'text-gray-900' : 'text-gray-500'}`}>{item.name}</span></td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{item.cif}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">{item.iban ? `${item.iban.slice(0, 4)}...${item.iban.slice(-4)}` : '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600">{item.linkedSitesCount ?? 0}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button onClick={() => handleToggleStatus(item)} className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${item.isActive ? 'bg-blue-600' : 'bg-gray-300'}`} role="switch" aria-checked={item.isActive}>
                          <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${item.isActive ? 'translate-x-5' : 'translate-x-0'}`}>
                            <Power className={`h-3 w-3 absolute top-1 left-1 ${item.isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                          </span>
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => handleOpenLinkModal(item)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="Vincular a centros"><Link2 className="h-4 w-4" /></button>
                          <button onClick={() => handleEdit(item)} className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded" title="Editar"><Edit2 className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {meta && meta.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">Mostrando {(meta.page - 1) * meta.limit + 1} a {Math.min(meta.page * meta.limit, meta.total)} de {meta.total}</p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPage(meta.page - 1)} disabled={!meta.hasPrevPage || isLoading} className="p-2 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-50"><ChevronLeft className="h-4 w-4" /></button>
                  <span className="text-sm text-gray-700">Página {meta.page} de {meta.totalPages}</span>
                  <button onClick={() => setPage(meta.page + 1)} disabled={!meta.hasNextPage || isLoading} className="p-2 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-50"><ChevronRight className="h-4 w-4" /></button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Modal isOpen={isModalOpen} onClose={handleCloseModal} title={editingItem ? 'Editar sociedad' : 'Nueva sociedad'} subtitle={editingItem ? `Editando: ${editingItem.name}` : 'Completa los datos'} size="lg">
        <form onSubmit={handleSubmit(handleSave)}>
          {/* Error banner dentro del modal */}
          {formError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="h-4 w-4 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-red-800">Error de validación</p>
                <p className="text-sm text-red-600 mt-0.5">{formError}</p>
              </div>
              <button
                type="button"
                onClick={() => setFormError(null)}
                className="flex-shrink-0 p-1 text-red-400 hover:text-red-600 rounded"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Código *</label>
                <input
                  type="text"
                  maxLength={20}
                  {...register('code', { 
                    required: 'El código es obligatorio',
                    maxLength: { value: 20, message: 'Máximo 20 caracteres' }
                  })}
                  onChange={() => formError && setFormError(null)}
                  className={`w-full px-4 py-2.5 border rounded-xl text-sm uppercase ${errors.code || (formError && formError.includes('código')) ? 'border-red-300 bg-red-50/50' : 'border-gray-200'}`}
                  placeholder="SOC001"
                />
                {errors.code && <p className="mt-1 text-xs text-red-500"><AlertCircle className="h-3 w-3 inline mr-1" />{errors.code.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CIF *</label>
                <input
                  type="text"
                  maxLength={20}
                  {...register('cif', { 
                    required: 'El CIF es obligatorio', 
                    minLength: { value: 9, message: 'CIF inválido' },
                    maxLength: { value: 20, message: 'Máximo 20 caracteres' }
                  })}
                  onChange={() => formError && setFormError(null)}
                  className={`w-full px-4 py-2.5 border rounded-xl text-sm uppercase ${errors.cif || (formError && formError.includes('CIF')) ? 'border-red-300 bg-red-50/50' : 'border-gray-200'}`}
                  placeholder="B12345678"
                />
                {errors.cif && <p className="mt-1 text-xs text-red-500"><AlertCircle className="h-3 w-3 inline mr-1" />{errors.cif.message}</p>}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre comercial *</label>
              <input
                type="text"
                maxLength={100}
                {...register('name', { 
                  required: 'El nombre es obligatorio',
                  maxLength: { value: 100, message: 'Máximo 100 caracteres' }
                })}
                className={`w-full px-4 py-2.5 border rounded-xl text-sm ${errors.name ? 'border-red-300' : 'border-gray-200'}`}
                placeholder="Clínica Dental S.L."
              />
              {errors.name && <p className="mt-1 text-xs text-red-500"><AlertCircle className="h-3 w-3 inline mr-1" />{errors.name.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Razón social</label>
              <input
                type="text"
                maxLength={200}
                {...register('legalName', {
                  maxLength: { value: 200, message: 'Máximo 200 caracteres' }
                })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm"
                placeholder="Razón social completa"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Banco</label>
                <input
                  type="text"
                  maxLength={100}
                  {...register('bankName', {
                    maxLength: { value: 100, message: 'Máximo 100 caracteres' }
                  })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm"
                  placeholder="Banco Santander"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">IBAN</label>
                <input
                  type="text"
                  maxLength={34}
                  {...register('iban', { 
                    validate: validateIBAN,
                    maxLength: { value: 34, message: 'Máximo 34 caracteres' }
                  })}
                  className={`w-full px-4 py-2.5 border rounded-xl text-sm uppercase font-mono ${errors.iban ? 'border-red-300' : 'border-gray-200'}`}
                  placeholder="ES91 2100 0418 4502 0005 1332"
                />
                {errors.iban && <p className="mt-1 text-xs text-red-500"><AlertCircle className="h-3 w-3 inline mr-1" />{errors.iban.message}</p>}
              </div>
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-700"><strong>💡 Tip:</strong> Para marcar esta sociedad como "por defecto" en un centro, usa el botón de vincular centros (🔗) en la tabla.</p>
            </div>
          </div>
          <ModalFooter>
            <button type="button" onClick={handleCloseModal} disabled={isSaving} className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"><X className="h-4 w-4" />Cancelar</button>
            <button type="submit" disabled={isSaving} className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-50 flex items-center gap-2">
              {isSaving ? <><Loader2 className="h-4 w-4 animate-spin" />Guardando...</> : <><Save className="h-4 w-4" />{editingItem ? 'Guardar' : 'Crear'}</>}
            </button>
          </ModalFooter>
        </form>
      </Modal>

      <Modal isOpen={isLinkModalOpen} onClose={handleCloseLinkModal} title="Vincular a centros" subtitle={linkingSociety ? `Sociedad: ${linkingSociety.name}` : ''} size="md">
        {isLoadingLinks ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
        ) : (
          <>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {linkedSites.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No hay centros disponibles</p>
              ) : (
                linkedSites.map((site) => (
                  <div key={site.siteId} className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${site.isLinked ? 'border-blue-200 bg-blue-50/50' : 'border-gray-200 hover:bg-gray-50'}`}>
                    <div className="flex items-center gap-3">
                      <button type="button" onClick={() => handleToggleSiteLink(site.siteId)} className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${site.isLinked ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300 hover:border-blue-400'}`}>
                        {site.isLinked && <Check className="h-3 w-3" />}
                      </button>
                      <div>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-gray-400" />
                          <span className="font-medium text-gray-900">{site.siteName}</span>
                          <span className="text-xs text-gray-500">({site.siteCode})</span>
                        </div>
                      </div>
                    </div>
                    {site.isLinked && (
                      <button type="button" onClick={() => handleToggleSiteDefault(site.siteId)} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${site.isDefault ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} title={site.isDefault ? 'Es sociedad por defecto en este centro' : 'Marcar como sociedad por defecto'}>
                        <Star className={`h-3 w-3 ${site.isDefault ? 'fill-green-500' : ''}`} /> {site.isDefault ? 'Por defecto' : 'Marcar default'}
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-700"><strong>⚠️ Importante:</strong> Cada centro debe tener una sociedad por defecto para poder crear presupuestos.</p>
            </div>
            <ModalFooter>
              <button type="button" onClick={handleCloseLinkModal} disabled={isSavingLinks} className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"><X className="h-4 w-4" />Cancelar</button>
              <button type="button" onClick={handleSaveLinks} disabled={isSavingLinks} className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-50 flex items-center gap-2">
                {isSavingLinks ? <><Loader2 className="h-4 w-4 animate-spin" />Guardando...</> : <><Save className="h-4 w-4" />Guardar vinculaciones</>}
              </button>
            </ModalFooter>
          </>
        )}
      </Modal>

      <ImportExportModal
        isOpen={isImportExportOpen}
        onClose={() => setIsImportExportOpen(false)}
        title="Sociedades"
        onExport={societiesImportExport.export}
        onDownloadTemplate={societiesImportExport.downloadTemplate}
        onPreview={societiesImportExport.preview}
        onImport={societiesImportExport.import}
        onImportSuccess={loadData}
        previewColumns={[
          { key: 'code', label: 'Código' },
          { key: 'name', label: 'Nombre' },
          { key: 'cif', label: 'CIF' },
        ]}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => !isProcessing && setConfirmDialog({ isOpen: false, item: null, action: 'deactivate' })}
        onConfirm={handleConfirmToggle}
        isLoading={isProcessing}
        variant={confirmDialog.action === 'deactivate' ? 'warning' : 'info'}
        title={confirmDialog.action === 'deactivate' ? '¿Desactivar sociedad?' : '¿Activar sociedad?'}
        message={confirmDialog.action === 'deactivate' ? `La sociedad "${confirmDialog.item?.name}" será desactivada.` : `La sociedad "${confirmDialog.item?.name}" será reactivada.`}
        confirmText={confirmDialog.action === 'deactivate' ? 'Desactivar' : 'Activar'}
      />
    </div>
  );
}

export default SocietiesAdminPage;
